import { useState } from "react";
import { NotificationsNoneOutlined } from "@mui/icons-material";
import { IconButton, Popover } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "../utils/portalHelpers";
import { partnerQueryKeys } from "../api/queryKeys";
import {
  fetchPartnerNotifications,
  markAllPartnerNotificationsRead,
  markPartnerNotificationRead,
} from "../api/endpoints/notifications";
import usePartnerQuery from "../hooks/usePartnerQuery";
import { alertLabel, alertTone, badgeCount } from "../pages/Notifications/notificationHelpers";
import { StatusChip } from "./ui";

const PAGE_SIZE = 8;

const READ_FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "read", label: "Read" },
];

/**
 * The portal's bell: the same idea as the admin one, in the portal's own chrome. It reads the
 * partner's own alerts and hands each one the destination the server resolved for it.
 */
const NotificationBell = ({ unreadCount = 0 }) => {
  const [anchor, setAnchor] = useState(null);
  const [readFilter, setReadFilter] = useState("all");
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const open = Boolean(anchor);

  const params = {
    page,
    size: PAGE_SIZE,
    ...(readFilter === "unread" ? { unreadOnly: true } : {}),
    ...(readFilter === "read" ? { readOnly: true } : {}),
  };

  const { data } = usePartnerQuery(
    partnerQueryKeys.notifications(params),
    () => fetchPartnerNotifications(params),
    { enabled: open, keepPreviousData: true }
  );

  const refresh = () => {
    queryClient.invalidateQueries([...partnerQueryKeys.all, "notifications"]);
    queryClient.invalidateQueries(partnerQueryKeys.notificationCount());
  };

  const markOne = useMutation(markPartnerNotificationRead, { onSuccess: refresh });
  const markAll = useMutation(markAllPartnerNotificationsRead, { onSuccess: refresh });

  const rows = data?.content || [];
  const totalPages = data?.totalPages ?? 0;
  const badge = badgeCount(unreadCount);

  const go = (notification) => {
    if (!notification.read) markOne.mutate(notification.id);
    close();
    if (notification.link) navigate(notification.link);
  };

  const close = () => {
    setAnchor(null);
    setPage(0);
  };

  return (
    <>
      <IconButton
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label={badge ? `Notifications, ${badge} unread` : "Notifications"}
        sx={{ color: "#0f4c4c" }}
      >
        <span className="relative inline-flex">
          <NotificationsNoneOutlined />
          {badge ? (
            <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
              {badge}
            </span>
          ) : null}
        </span>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: "min(92vw, 380px)", borderRadius: "16px" } }}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-bold text-slate-900 m-0">Notifications</p>
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isLoading || !unreadCount}
            className="text-xs font-semibold text-teal-700 bg-transparent border-0 cursor-pointer disabled:opacity-40"
          >
            Mark all read
          </button>
        </div>
        <div
          role="group"
          aria-label="Filter by read state"
          className="flex gap-1 px-4 py-2 border-b border-slate-100"
        >
          {READ_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type="button"
              aria-pressed={readFilter === filter.id}
              onClick={() => {
                setReadFilter(filter.id);
                setPage(0);
              }}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                readFilter === filter.id
                  ? "bg-teal-700 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-slate-500 px-4 py-6 m-0 text-center">
            {readFilter === "unread"
              ? "Nothing unread."
              : readFilter === "read"
              ? "No read notifications."
              : "Nothing yet."}
          </p>
        ) : (
          <ul className="list-none p-0 m-0 max-h-[60vh] overflow-y-auto">
            {rows.map((notification) => (
              <li key={notification.id} className="border-b border-slate-100 last:border-0">
                <button
                  type="button"
                  onClick={() => go(notification)}
                  className={`w-full text-left px-4 py-3 bg-transparent border-0 cursor-pointer ${
                    notification.read ? "" : "bg-teal-50/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-sm m-0 ${
                        notification.read ? "text-slate-700" : "font-semibold text-slate-900"
                      }`}
                    >
                      {notification.content}
                    </p>
                    <StatusChip tone={alertTone(notification)}>
                      {alertLabel(notification)}
                    </StatusChip>
                  </div>
                  <p className="text-[11px] text-slate-400 m-0 mt-1">
                    {formatDateTime(notification.createdAt)}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 ? (
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-slate-100">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(current - 1, 0))}
              className="text-xs font-semibold text-teal-700 bg-transparent border-0 cursor-pointer disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-[11px] text-slate-500">
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((current) => current + 1)}
              className="text-xs font-semibold text-teal-700 bg-transparent border-0 cursor-pointer disabled:opacity-40"
            >
              Next
            </button>
          </div>
        ) : null}
        <div className="px-4 py-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => {
              close();
              navigate("/partner/notifications");
            }}
            className="w-full rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white border-0 cursor-pointer"
          >
            See all notifications
          </button>
        </div>
      </Popover>
    </>
  );
};

export default NotificationBell;
